/**
 * Date formatting, porting the `intl` `DateFormat` patterns used across
 * the app. Built on `Intl.DateTimeFormat` — no date library needed for
 * fixed-pattern display formatting.
 */

// UTC, not the viewer's local zone: `transaction_date` timestamps are
// parsed with Dart's `DateTime.parse` in the Flutter app and formatted
// without ever calling `.toLocal()`, so its transaction-table dates are the
// UTC calendar date of the stored instant. `Intl.DateTimeFormat` defaults
// to the runtime's local zone, which silently shifts a transaction near a
// UTC day boundary onto the wrong day for any viewer not in UTC — pinning
// this to "UTC" keeps the two apps' transaction-row dates identical.
const shortDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "2-digit",
  day: "2-digit",
  year: "2-digit",
  timeZone: "UTC",
});

const mediumDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
});

const monthDayFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});

const monthShortFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
});

const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/** `06/10/26` — transaction table rows. */
export function formatShortDate(date: Date): string {
  return shortDateFormatter.format(date);
}

/** `Jun 10, 2026` — report headers and date ranges. */
export function formatMediumDate(date: Date): string {
  return mediumDateFormatter.format(date);
}

/** `Jun 10` — week names. */
export function formatMonthDay(date: Date): string {
  return monthDayFormatter.format(date);
}

/** `Sep` — month-to-date dashboard tile labels. */
export function formatMonthShort(date: Date): string {
  return monthShortFormatter.format(date);
}

// Some ICU versions insert a narrow no-break space (code point 0x202F)
// before AM/PM instead of a regular space. Written as a fromCharCode call
// rather than a \u escape or literal character so the source file itself
// contains no raw non-ASCII whitespace for editors/linters to trip on.
const NARROW_NO_BREAK_SPACE = String.fromCharCode(0x202f);

/**
 * `Jun 10, 2026, 3:45 PM` — "generated at" / "issued at" stamps, with any
 * narrow no-break space before AM/PM normalized to a regular space so
 * output is consistent across browsers and doesn't look like a rendering
 * glitch when copied.
 */
export function formatDateTime(date: Date): string {
  return dateTimeFormatter.format(date).split(NARROW_NO_BREAK_SPACE).join(" ");
}

/** `Date` → `"yyyy-mm-dd"`, for a native `<input type="date">`'s `value`/`min`/`max`. */
export function toDateInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/**
 * `"yyyy-mm-dd"` → local midnight `Date` — `new Date("yyyy-mm-dd")` parses
 * as UTC, which shifts the day in negative UTC offsets (a real bug the
 * Flutter app had; avoided here).
 */
export function parseDateInputValue(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year as number, (month as number) - 1, day);
}
